import {
	Alert,
	Badge,
	Box,
	Button,
	Icons,
	Inline,
	Input,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useEffect, useState } from "react"
import {
	QUICK_SEND_MESSAGE_TYPE_LABELS,
	useSearchWAQuickSends,
	useWAQuickSendRun,
	type TWAQuickSend,
	type TWAQuickSendResult,
} from "../../WhatsAppQuickSends/store"
import { QuickSendUsage, WhatsAppQuickSendPreview } from "./QuickSendPreview"

/** The pause in typing after which the picker searches, as the inbox does. */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Pick a quick send and send it into the conversation.
 *
 * Picking one never sends it. It opens a preview of every message in the
 * order it goes out, and only the preview's Send button sends, since the
 * agent is about to put several messages in front of a customer at once.
 */
export function SendQuickSendDialog({
	open,
	onClose,
	recipient,
	outsideServiceWindow,
	onSend,
	onSent,
}: {
	open: boolean
	onClose: () => void
	/** Who the messages go to, as the agent knows them. */
	recipient: string
	/** The last message from the customer is more than 24 hours old. */
	outsideServiceWindow?: boolean
	onSend: (payload: { wa_quick_send_id: number }) => Promise<TWAQuickSendResult>
	onSent?: () => void | Promise<void>
}) {
	const [term, setTerm] = useState("")
	// What is searched for: the typed term once typing pauses, so a search
	// is not sent for every keystroke.
	const [query, setQuery] = useState("")
	const [picked, setPicked] = useState<TWAQuickSend | null>(null)
	const [sending, setSending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	// The sending, once its first message is out. The rest go one by one,
	// each once WhatsApp reports the one before it sent.
	const [started, setStarted] = useState<TWAQuickSendResult | null>(null)
	const { data: quickSends, isLoading } = useSearchWAQuickSends(open, query)

	useEffect(() => {
		const timer = window.setTimeout(
			() => setQuery(term.trim()),
			SEARCH_DEBOUNCE_MS
		)
		return () => window.clearTimeout(timer)
	}, [term])
	const { data: run } = useWAQuickSendRun(started)
	const runStatus = run?.data.status
	const runSent = run?.data.sent ?? 0

	// Every opening starts from the list, with nothing picked.
	useEffect(() => {
		if (!open) {
			setTerm("")
			setQuery("")
			setPicked(null)
			setError(null)
			setStarted(null)
		}
	}, [open])

	// The thread picks up each message as it goes out.
	useEffect(() => {
		if (runSent > 0) {
			onSent?.()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [runSent])

	// Done: say so and get out of the way. A failure stays on screen, and
	// offers no second send, which would repeat what already went out.
	useEffect(() => {
		if (runStatus === "completed" && run) {
			showSnackbar(run.message)
			onClose()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [runStatus])

	const send = async () => {
		if (!picked || sending || started) return

		setSending(true)
		setError(null)

		try {
			setStarted(await onSend({ wa_quick_send_id: picked.id }))
		} catch (e) {
			const response = (e as { response?: { data?: { message?: string } } })
				.response
			setError(
				response?.data?.message ||
					(e as Error).message ||
					"The quick send could not be sent."
			)
		} finally {
			setSending(false)
		}
	}

	return (
		<Dialog
			open={open}
			onClose={sending ? () => undefined : onClose}
			title={picked ? picked.name : "Send a Quick Send"}
			sm
		>
			<Dialog.Body>
				{picked ? (
					<Stack gap="4">
						<Alert status="info">
							<Text>
								These {picked.messages?.length ?? 0} messages go to {recipient},
								in this order, as soon as you send.
							</Text>
						</Alert>
						{outsideServiceWindow ? (
							<Alert status="warning">
								The customer has not written in the last 24 hours, so WhatsApp
								will refuse these messages. Send an approved template to reopen
								the conversation first.
							</Alert>
						) : null}
						<WhatsAppQuickSendPreview quickSend={picked} />
						{error ? <Alert status="error">{error}</Alert> : null}
						{run && runStatus === "sending" ? (
							<Alert status="info">
								<Text>
									Sent {runSent} of {run.data.total}. Each message goes out once
									WhatsApp confirms the one before, so the customer reads them
									in order. You can close this; the rest keep going.{" "}
									<Icons.Refresh spin />
								</Text>
							</Alert>
						) : null}
						{run && runStatus === "failed" ? (
							<Alert status="warning">
								<Stack gap="1">
									<Text>{run.message}</Text>
									<Text>
										The messages before it are in the conversation. Send the
										rest yourself.
									</Text>
								</Stack>
							</Alert>
						) : null}
						{started ? (
							<Box>
								<Button onClick={onClose}>Close</Button>
							</Box>
						) : (
							<Inline gap="2">
								<Button level="primary" disabled={sending} onClick={send}>
									{sending ? "Sending..." : "Send"}
								</Button>
								<Button
									disabled={sending}
									onClick={() => {
										setPicked(null)
										setError(null)
									}}
								>
									Back
								</Button>
							</Inline>
						)}
					</Stack>
				) : (
					<Stack gap="3">
						<Input
							type="search"
							value={term}
							size="sm"
							autoFocus
							onChange={(e) => setTerm(String(e.currentTarget.value))}
							placeholder="Search quick sends"
						/>
						{isLoading ? (
							<Spinner alignCenter padding="4" />
						) : !quickSends?.length ? (
							<Alert status="info">
								{query
									? "No quick send matches that name."
									: "No quick sends yet. Create them from a WhatsApp integration's Quick Sends tab."}
							</Alert>
						) : (
							<Stack as="ul" gap="2">
								{quickSends.map((quickSend) => (
									<Box as="li" key={quickSend.id}>
										<Box
											as="button"
											type="button"
											width="full"
											textAlign="left"
											borderWidth="1"
											rounded="md"
											padding="3"
											cursor="pointer"
											onClick={() => setPicked(quickSend)}
										>
											<Stack gap="1">
												<Text fontWeight="semibold">{quickSend.name}</Text>
												<Inline gap="1" flexWrap="wrap" alignItems="center">
													{(quickSend.messages ?? []).map((message) => (
														<Badge key={message.id}>
															{QUICK_SEND_MESSAGE_TYPE_LABELS[message.type]}
														</Badge>
													))}
													<QuickSendUsage quickSend={quickSend} />
												</Inline>
											</Stack>
										</Box>
									</Box>
								))}
							</Stack>
						)}
						<Box>
							<Button onClick={onClose}>Cancel</Button>
						</Box>
					</Stack>
				)}
			</Dialog.Body>
		</Dialog>
	)
}
